import { useState } from 'react';
import { Stack, Text, Center, Skeleton, UnstyledButton } from '@mantine/core';
import { IconPlayerPlay, IconPhotoOff } from '@tabler/icons-react';
import { useProtectedMediaUrl, mediaUrl } from '../../api';

// Renders a single seller-media item inside a positioned parent (fills 100%).
// Extracted from SellerMediaGallery's MediaCard so the album pages share the
// same rendering rules for auth-gated files (contract §5):
//
// - Protected images fetch eagerly through GET /api/v1/seller-media/{id}/file
//   and render the object URL (skeleton while loading, neutral placeholder on
//   failure).
// - Protected videos never fetch eagerly — the no-store route is a full
//   download into memory — they show a play tile and fetch on click, then
//   render with autoPlay.
// - Unprotected items (isProtected false: public + published) keep the plain,
//   cacheable mediaUrl(file_path) and skip the authenticated fetch entirely.
const ProtectedMediaItem = ({ mediaId, mediaType, title, alt, filePath, isProtected = true }) => {
  const isVideo = mediaType === 'video';
  const [playRequested, setPlayRequested] = useState(false);
  const wantsFetch = isProtected && (!isVideo || playRequested);
  const { url: protectedUrl, error: protectedError } =
    useProtectedMediaUrl(mediaId, { enabled: wantsFetch });
  const url = isProtected ? protectedUrl : mediaUrl(filePath);

  if (isProtected && isVideo && !playRequested) {
    return (
      <UnstyledButton
        onClick={() => setPlayRequested(true)}
        aria-label="Play video"
        style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Stack align="center" gap={4}>
          <IconPlayerPlay size="1.6rem" color="var(--mantine-color-gray-3)" />
          <Text size="xs" c="gray.4" px={4} lineClamp={1}>{title || 'Video'}</Text>
        </Stack>
      </UnstyledButton>
    );
  }

  if (wantsFetch && protectedError) {
    return (
      <Center h="100%">
        <Stack align="center" gap={4}>
          <IconPhotoOff size="1.4rem" color="var(--mantine-color-gray-5)" />
          <Text size="xs" c="dimmed">Preview unavailable</Text>
        </Stack>
      </Center>
    );
  }

  if (wantsFetch && !url) {
    // Loading covers the whole no-URL-yet window, including the frame between
    // isProtected flipping true and the hook's fetch starting.
    return <Skeleton height="100%" radius={0} data-testid={`media-skeleton-${mediaId}`} />;
  }

  if (isVideo) {
    return (
      <video
        src={url}
        controls
        autoPlay={isProtected && playRequested}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }

  return (
    <img
      src={url}
      alt={alt || title || 'Media'}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
};

export default ProtectedMediaItem;
